import type { Statemachine } from 'overmind';
import { statemachine } from 'overmind';

export type User = {
  name: string;
  preferredUsername: string;
  token: string;
  identityProvider: string;
};

type States =
  | { current: 'NOT_INITIALIZED' }
  | {
      current: 'AUTHENTICATING';
    }
  | {
      current: 'UNAUTHENTICATED';
    }
  | {
      current: 'AUTHENTICATED';
      user: User;
      hasVolueAccessTokenExpired?: boolean;
      arcgisToken: string;
    }
  | { current: 'AWAITING_ARCGIS_AUTHENTICATION'; user: User }
  | { current: 'AWAITING_FEATURE_TOGGLES'; user: User; redirecting: boolean } // TODO: new state should be introduced - but maybe better split machines...
  | { current: 'FEATURE_TOGGLES_IN_PROGRESS'; user: User; redirecting: boolean } // TODO: new state should be introduced - but maybe better split machines...
  | { current: 'ERROR'; error: string }
  | { current: 'AUTHENTICATING_ARCGIS'; user: User; redirecting: boolean }; // TODO: new state should be introduced - but maybe better split machines...

type Events =
  | {
      type: 'NO_EXTERNALLY_AVAILABLE_ARCGIS_TOKEN';
    }
  | {
      type: 'INITIALIZE';
    }
  | {
      type: 'AUTHENTICATE';
    }
  | {
      type: 'SIGN_OUT';
    }
  | {
      type: 'AUTHENTICATION_ERROR';
      data: string;
    }
  | {
      type: 'AUTHENTICATION_SUCCESS';
      data: User;
    }
  | {
      type: 'AUTHENTICATE_ARCGIS';
    }
  | {
      type: 'AUTHENTICATE_ARCGIS_MANUALLY';
      data: string | undefined;
    }
  | {
      type: 'ARCGIS_AUTHENTICATION_COMPLETED';
      data: string;
    }
  | {
      type: 'ARCGIS_AUTHENTICATION_FAILED';
      data: string;
    }
  | {
      type: 'ARCGIS_AUTHENTICATING';
    }
  | {
      type: 'ARCGIS_AUTHENTICATING_FROM_REDIRECTION';
    }
  | {
      type: 'FEATURE_TOGGLES_RESET';
    }
  | {
      type: 'TOKEN_AVAILABLE_EXTERNALLY';
      data: {
        arcgisToken: string;
      };
    }
  | {
      type: 'FEATURE_TOGGLES_READY';
    }
  | {
      type: 'STOP_AUTHENTICATING';
    }
  | {
      type: 'ACCESS_TOKEN_EXPIRED';
    }

export type AuthMachine = Statemachine<States, Events>;

const transition = <K extends States['current']>({
  to,
  withState
}: {
  to: K;
  withState?: any;
}) => ({
  current: to,
  ...Object.assign({}, JSON.parse(JSON.stringify(withState || {})))
});

export const authMachine = statemachine<States, Events>({
  AWAITING_ARCGIS_AUTHENTICATION: {
    ARCGIS_AUTHENTICATING: (_, state) =>
      transition({
        to: 'AUTHENTICATING_ARCGIS',
        withState: {
          user: state.user
        }
      }),
    AUTHENTICATE_ARCGIS: (_, state) =>
      transition({
        to: 'AWAITING_FEATURE_TOGGLES',
        withState: {
          user: state.user,
          fromState: 'AUTHENTICATE_ARCGIS' // TODO: check if it's correct. This is event name - not state
        }
      }),
    TOKEN_AVAILABLE_EXTERNALLY: (event, state) => {
      if (event.arcgisToken) {
        return transition({
          to: 'AUTHENTICATED',
          withState: {
            user: state.user,
            arcgisToken: event.arcgisToken
          }
        });
      }
    }
  },
  AWAITING_FEATURE_TOGGLES: {
    FEATURE_TOGGLES_RESET: (_, state) =>
      transition({
        to: 'FEATURE_TOGGLES_IN_PROGRESS',
        withState: {
          user: state.user,
          redirecting: state.redirecting
        }
      })
  },
  FEATURE_TOGGLES_IN_PROGRESS: {
    FEATURE_TOGGLES_READY: (_, state) => {
      return transition({
        to: 'AUTHENTICATING_ARCGIS',
        withState: {
          user: state.user,
          redirecting: state.redirecting
        }
      });
    }
  },
  AUTHENTICATING_ARCGIS: {
    NO_EXTERNALLY_AVAILABLE_ARCGIS_TOKEN: (_, state) => {
      return transition({
        to: 'AWAITING_ARCGIS_AUTHENTICATION',
        withState: {
          user: state.user
        }
      });
    },
    STOP_AUTHENTICATING: (_, state) =>
      transition({
        to: 'AWAITING_ARCGIS_AUTHENTICATION',
        withState: {
          user: state.user
        }
      }),
    ARCGIS_AUTHENTICATION_COMPLETED: (arcgisToken, state) =>
      transition({
        to: 'AUTHENTICATED',
        withState: {
          user: state.user,
          arcgisToken
        }
      }),
    ARCGIS_AUTHENTICATION_FAILED: error => ({
      current: 'ERROR',
      error
    }),
    AUTHENTICATION_SUCCESS: (user, state) => {
      // only volue success in redirect but arcgis not!
      return transition({
        to: 'AWAITING_FEATURE_TOGGLES',
        withState: {
          user,
          fromState: 'AUTHENTICATING_ARCGIS',
          redirecting: state.redirecting
        }
      });
    },
    AUTHENTICATE_ARCGIS_MANUALLY: (arcgisToken, state) => {
      if (arcgisToken) {
        return transition({
          to: 'AUTHENTICATED',
          withState: {
            user: state.user,
            arcgisToken
          }
        });
      }

      return transition({
        to: 'AWAITING_ARCGIS_AUTHENTICATION',
        withState: {
          user: state.user,
          fromState: 'AUTHENTICATING_ARCGIS',
          redirecting: state.redirecting
        }
      });
    }
  },
  NOT_INITIALIZED: {
    INITIALIZE: () => ({ current: 'UNAUTHENTICATED' })
  },
  UNAUTHENTICATED: {
    AUTHENTICATE: () => ({ current: 'AUTHENTICATING' })
  },
  AUTHENTICATING: {
    ARCGIS_AUTHENTICATING: () =>
      transition({
        to: 'AUTHENTICATING_ARCGIS'
      }),
    ARCGIS_AUTHENTICATING_FROM_REDIRECTION: () =>
      transition({
        to: 'AUTHENTICATING_ARCGIS',
        withState: {
          redirecting: true
        }
      }),
    AUTHENTICATION_ERROR: error => ({
      current: 'ERROR',
      error
    }),
    AUTHENTICATION_SUCCESS: user =>
      transition({
        // volue identity authenticated
        to: 'AWAITING_FEATURE_TOGGLES',
        withState: {
          user,
          fromState: 'AUTHENTICATING'
        }
      })
  },
  AUTHENTICATED: {
    AUTHENTICATE_ARCGIS: (_, state) => {
      return transition({
        to: 'AWAITING_FEATURE_TOGGLES',
        withState: {
          user: state.user,
          fromState: 'AUTHENTICATED'
        }
      });
    },
    SIGN_OUT: () => ({
      current: 'UNAUTHENTICATED'
    }),
    AUTHENTICATION_ERROR: error => ({
      current: 'ERROR',
      error
    }),
    ACCESS_TOKEN_EXPIRED: (_, state) => 
    {
      return transition({
        to: "AUTHENTICATED",
        withState: {
          user: state.user,
          hasVolueAccessTokenExpired: true,
          arcgisToken: state.arcgisToken
        }
      })
    }
  },
  ERROR: {}
});

type InOneOf<AuthMachine, TStates extends States['current']> = Extract<
  AuthMachine,
  { current: TStates }
>;
export const isInProgress = (
  state: AuthMachine
): state is InOneOf<
  AuthMachine,
  'AUTHENTICATED' | 'AUTHENTICATING_ARCGIS' | 'AWAITING_ARCGIS_AUTHENTICATION'
> =>
  state.current === 'AUTHENTICATING' ||
  state.current === 'AUTHENTICATING_ARCGIS' ||
  state.current === 'AWAITING_ARCGIS_AUTHENTICATION';

export function isUserSignedInToVolueIdentity(
  state: AuthMachine
): state is InOneOf<
  AuthMachine,
  | 'AUTHENTICATED'
  | 'AWAITING_ARCGIS_AUTHENTICATION'
  | 'AUTHENTICATING_ARCGIS'
  | 'AWAITING_FEATURE_TOGGLES'
  | 'FEATURE_TOGGLES_IN_PROGRESS'
> {
  return (
    state.current === 'AUTHENTICATED' ||
    state.current === 'AWAITING_ARCGIS_AUTHENTICATION' ||
    state.current === 'AUTHENTICATING_ARCGIS' ||
    state.current === 'AWAITING_FEATURE_TOGGLES' ||
    state.current === 'FEATURE_TOGGLES_IN_PROGRESS'
  );
}

export const createAuthMachine = () => {
  return authMachine.create({ current: 'NOT_INITIALIZED' });
};
